import { c as _c } from "react/compiler-runtime";
import { memo, useCallback, useMemo } from 'react';
import { Box, Center, Text } from '@chakra-ui/react';
import ShimmerProgressBar from '@/components/util-component/ShimmerProgressBar';
import { VariableStateSelectEntry } from '@/services/db/schema';
import { VariableStateId } from '@/typeAlias';
import { Interval } from '@/types/variable';
import { ParameterizationType } from '@/types/variable';
import getContrastingFontColor from '@/utils/general/getContrastingFontColor';
import { formatAbsoluteImpact, formatRelativeImpact } from '@/utils/impact/formatImpact';
import getParameterization from '@/utils/network/getParameterization';
import BarChart, { calculateBarChartHeight } from './BarChart';
import { NodeWarning } from '../NodeWarnings/nodeWarning';
import NodeWarningBadge from '../NodeWarnings/NodeWarningBadge';
export type VariableWithBarChartProps = {
  variable: {
    name: string;
    states: BarChartState[];
    comment: string;
    tags?: string[] | null;
    color: string;
    intervals: Interval[] | undefined;
    optimalStateId: string | null;
    parameterization: ParameterizationType;
    probabilities: number[];
    impact?: number | null;
    isRelativeImpact?: boolean;
    warnings?: NodeWarning[];
    variableStatesWithMetadata: Record<VariableStateId, VariableStateSelectEntry>;
  };
  selected: boolean;
  assumption: number | undefined;
  toggleAssumption: (stateId: VariableStateId) => void;
  onDoubleClick: () => void;
  onSilenceWarning?: (warning: NodeWarning) => Promise<void> | void;
  /** User-adjustable node width (from global state). Defaults to NODE_WIDTH. */
  width?: number;
};
export type BarChartState = {
  stateId: VariableStateId;
  probability: number;
};
export const NODE_WIDTH = 180;
const titleLineHeight = 14;
const headerPaddingY = 6;
const padding = 6;
const borderThickness = 1;
const NODE_BORDER_COLOR = '#474e68';
const NODE_SELECTED_COLOR = '#2563eb';
// Lowest opacity a dimmed (low/zero-impact) node fades to, so it never fully
// disappears while its edges still point at it.
const MIN_IMPACT_OPACITY = 0.15;
// Selected (and hovered) nodes get a bold blue focus ring that sits
// outside the node (a white separator keeps it legible on any node
// color, then a thick blue band, then a drop shadow for lift). It does
// not change the node's box size, so the edge intersection geometry is
// unaffected; arrowheads are gapped a few px off the border (see
// ARROW_END_GAP) so the ring doesn't hide their tips.
const NODE_HIGHLIGHT_SHADOW = `0 0 0 2px #ffffff, 0 0 0 5px ${NODE_SELECTED_COLOR}, 0 8px 22px rgba(0, 0, 0, 0.35)`;

// Every node uses the same fixed-height header so headers line up across
// the graph. The title wraps within it and clamps to TITLE_MAX_LINES
// (ellipsis beyond). Fixed height keeps calculateNodeHeight deterministic
// for the edge intersection geometry.
const TITLE_MAX_LINES = 3;
const titleBlockHeight = TITLE_MAX_LINES * titleLineHeight + 2 * headerPaddingY;
export const calculateNodeHeight = (stateCount: number): number => 2 * borderThickness + titleBlockHeight + 2 * padding + calculateBarChartHeight(stateCount);
const VariableWithBarChart = memo(t0 => {
  const $ = _c(74);
  const {
    variable,
    selected,
    assumption,
    toggleAssumption,
    onDoubleClick,
    onSilenceWarning,
    width: t1
  } = t0;
  const width = t1 === undefined ? NODE_WIDTH : t1;
  let t2;
  if ($[0] !== onDoubleClick) {
    t2 = e => {
      e.preventDefault();
      onDoubleClick();
    };
    $[0] = onDoubleClick;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleNodeDoubleClick = t2;
  let t3;
  if ($[2] !== variable.tags) {
    t3 = variable.tags?.includes("public") ?? false;
    $[2] = variable.tags;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isPublic = t3;
  let t4;
  if ($[4] !== variable.states) {
    t4 = variable.states.map(_temp);
    $[4] = variable.states;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const stateIds = t4;
  let t5;
  if ($[6] !== variable.states || $[7] !== variable.variableStatesWithMetadata) {
    let t6;
    if ($[9] !== variable.variableStatesWithMetadata) {
      t6 = state_0 => variable.variableStatesWithMetadata?.[state_0.stateId]?.name;
      $[9] = variable.variableStatesWithMetadata;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    t5 = variable.states.map(t6);
    $[6] = variable.states;
    $[7] = variable.variableStatesWithMetadata;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const stateNames = t5;
  let t6;
  if ($[11] !== stateIds || $[12] !== stateNames || $[13] !== variable.optimalStateId || $[14] !== variable.parameterization || $[15] !== variable.probabilities) {
    t6 = getParameterization(variable.parameterization, stateIds, stateNames, variable.optimalStateId, variable.probabilities);
    $[11] = stateIds;
    $[12] = stateNames;
    $[13] = variable.optimalStateId;
    $[14] = variable.parameterization;
    $[15] = variable.probabilities;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const parameterization = t6;
  const t7 = variable.color;
  let t8;
  if ($[17] !== variable.color) {
    t8 = getContrastingFontColor(variable.color);
    $[17] = variable.color;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8 || $[20] !== variable.color) {
    t9 = {
      typeColor: t7,
      typeContrastingColor: t8
    };
    $[19] = t8;
    $[20] = variable.color;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const colors = t9;
  const impactOpacity = selected || variable.impact === undefined || variable.impact === null ? 1 : Math.max(MIN_IMPACT_OPACITY, Math.pow(variable.impact, 0.25));
  let t10;
  if ($[22] !== selected) {
    t10 = selected ? undefined : {
      borderColor: NODE_SELECTED_COLOR,
      boxShadow: NODE_HIGHLIGHT_SHADOW
    };
    $[22] = selected;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = `node-${variable.name}`;
  const t12 = selected ? NODE_SELECTED_COLOR : NODE_BORDER_COLOR;
  const t13 = selected ? NODE_HIGHLIGHT_SHADOW : "base";
  let t14;
  if ($[24] !== variable.states.length) {
    t14 = calculateNodeHeight(variable.states.length);
    $[24] = variable.states.length;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = `node-header-${variable.name}`;
  const t16 = `Parameterization subtype: ${parameterization.name}`;
  let t17;
  if ($[26] !== parameterization.icon) {
    t17 = <Text backgroundColor="blackAlpha.800" borderRadius="50%" color="white" fontSize="xs" fontWeight="bold" h="14px" lineHeight="14px" textAlign="center" w="14px">{parameterization.icon}</Text>;
    $[26] = parameterization.icon;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t16 || $[29] !== t17) {
    t18 = <Box left="4px" position="absolute" title={t16} top="4px">{t17}</Box>;
    $[28] = t16;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== isPublic) {
    t19 = isPublic && <Box position="absolute" right="4px" title="Public tag" top="4px"><Text backgroundColor="blackAlpha.800" borderRadius="50%" color="white" fontSize="xs" fontWeight="bold" h="14px" lineHeight="14px" textAlign="center" w="14px">P</Text></Box>;
    $[31] = isPublic;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== isPublic || $[34] !== onSilenceWarning || $[35] !== variable.name || $[36] !== variable.warnings) {
    t20 = variable.warnings && variable.warnings.length > 0 && <Box position="absolute" right={isPublic ? "22px" : "4px"} top="4px"><NodeWarningBadge variableName={variable.name} warnings={variable.warnings} onSilence={onSilenceWarning} /></Box>;
    $[33] = isPublic;
    $[34] = onSilenceWarning;
    $[35] = variable.name;
    $[36] = variable.warnings;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: String(TITLE_MAX_LINES),
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== variable.name) {
    t22 = <Text css={t21} fontSize="xs" fontWeight="bold" lineHeight={`${titleLineHeight}px`} maxHeight={`${TITLE_MAX_LINES * titleLineHeight}px`} paddingX={5} textAlign="center" whiteSpace="normal" width="100%" wordBreak="break-word">{variable.name}</Text>;
    $[39] = variable.name;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== colors.typeColor || $[42] !== colors.typeContrastingColor || $[43] !== t15 || $[44] !== t18 || $[45] !== t19 || $[46] !== t20 || $[47] !== t22) {
    t23 = <Box alignItems="center" bgColor={colors.typeColor} color={colors.typeContrastingColor} data-testid={t15} display="flex" flexShrink={0} h={`${titleBlockHeight}px`} justifyContent="center" position="relative">{t18}{t19}{t20}{t22}</Box>;
    $[41] = colors.typeColor;
    $[42] = colors.typeContrastingColor;
    $[43] = t15;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== assumption || $[50] !== colors.typeColor || $[51] !== toggleAssumption || $[52] !== variable.color || $[53] !== variable.impact || $[54] !== variable.intervals || $[55] !== variable.isRelativeImpact || $[56] !== variable.optimalStateId || $[57] !== variable.states || $[58] !== variable.variableStatesWithMetadata) {
    t24 = variable.impact !== undefined ? variable.impact !== null && <Center bgColor={variable.color} borderColor={colors.typeColor} borderRadius={3} borderWidth={1} gridColumnGap={1} gridTemplateColumns="2fr 3fr 5fr" px={0.5}><Text fontSize="xs" w={`${variable.isRelativeImpact ? "240px" : "100px"}`}>{variable.isRelativeImpact ? "Rel. Impact" : "Impact"}</Text><Text fontSize="xs">{variable.isRelativeImpact ? formatRelativeImpact(variable.impact) : formatAbsoluteImpact(variable.impact)}</Text><ShimmerProgressBar percentage={Math.min(variable.impact * 100, 100)} /></Center> : <BarChart assumption={assumption} intervals={variable.intervals} optimalStateId={variable.optimalStateId} states={variable.states} toggleAssumption={toggleAssumption} variableStatesWithMetadata={variable.variableStatesWithMetadata} />;
    $[49] = assumption;
    $[50] = colors.typeColor;
    $[51] = toggleAssumption;
    $[52] = variable.color;
    $[53] = variable.impact;
    $[54] = variable.intervals;
    $[55] = variable.isRelativeImpact;
    $[56] = variable.optimalStateId;
    $[57] = variable.states;
    $[58] = variable.variableStatesWithMetadata;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t24) {
    t25 = <Box color="gray.800" display="flex" flex="1" flexDir="column" justifyContent="flex-start" px={`${padding}px`} py={`${padding}px`}>{t24}</Box>;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== handleNodeDoubleClick || $[63] !== impactOpacity || $[64] !== t10 || $[65] !== t11 || $[66] !== t12 || $[67] !== t13 || $[68] !== t14 || $[69] !== t23 || $[70] !== t25 || $[71] !== variable.name || $[72] !== width) {
    t26 = <Box _hover={t10} aria-label={t11} bgColor="white" border={`${borderThickness}px solid`} borderColor={t12} borderRadius={8} boxShadow={t13} display="flex" flexDir="column" h={t14} opacity={impactOpacity} overflow="hidden" title={variable.name} w={width} onDoubleClick={handleNodeDoubleClick}>{t23}{t25}</Box>;
    $[62] = handleNodeDoubleClick;
    $[63] = impactOpacity;
    $[64] = t10;
    $[65] = t11;
    $[66] = t12;
    $[67] = t13;
    $[68] = t14;
    $[69] = t23;
    $[70] = t25;
    $[71] = variable.name;
    $[72] = width;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  return t26;
});
VariableWithBarChart.displayName = 'VariableWithBarChart';
export default VariableWithBarChart;
function _temp(state) {
  return state.stateId;
}